import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath } from 'react-router-dom';
import { Table, Spin, Typography, Button, Input, InputNumber } from 'antd';
import { AiOutlineClose } from 'react-icons/ai';
import axiosInstance from '../../../../../interceptor/axiosInstance';
import { ApiRoutes } from '../../../../../routes/routeConstants/apiRoutes';
import { SpatialPreviewFeaturesResponse } from '../../../../../types/TemporaryPreview.types';
import Notification from '../../../../../shared/components/Notification';
import { NotificationTypes } from '../../../../../enums/notificationTypes';
import EditIcon from '../../../../../assets/icons/view-attribute-table-edit.svg';
import SaveIcon from '../../../../../assets/icons/view-attribute-table-save.svg';
import './PreviewAttributeTable.scss';

const { Title, Text } = Typography;

// Fetch 500 rows per request and append on scroll so large datasets never load
// into the browser at once (mirrors ViewAttributeTable's server-mode paging).
const PAGE_SIZE = 500;

interface ColumnDef {
  name: string;
  type: string;
  numeric: boolean;
}

interface PreviewAttributeTableProps {
  sessionId: string;
  title: string;
  onClose: () => void;
  // Edits are only possible (and only propagate anywhere) when this session is
  // bound to a real, already-published layer's table rather than a throwaway
  // preview_* table — see FileFullscreenPreview's `!preview.isTemporary`.
  editable?: boolean;
}

interface TableColumn {
  title: string;
  dataIndex: string;
  key: string;
  width: number;
  ellipsis?: boolean;
  render?: (value: unknown, record: Record<string, unknown>) => React.ReactNode;
}

// Ant Design Table renders primitives natively; objects/arrays crash React.
const renderCellValue = (value: unknown): React.ReactNode => {
  if (value == null) return value as null | undefined;
  const t = typeof value;
  if (['string', 'number', 'boolean'].includes(t)) return value as string | number | boolean;
  return 'Unsupported Content';
};

// gid is the row identity used to key updates back to the server — never editable.
const buildColumns = (
  columnDefs: ColumnDef[],
  editingEnabled: boolean,
  onCellChange: (gid: string, column: string, value: unknown) => void,
): TableColumn[] =>
  columnDefs.map((c) => ({
    title: c.name.replace(/_/g, ' ').toUpperCase(),
    dataIndex: c.name,
    key: c.name,
    width: 180,
    ellipsis: !editingEnabled,
    render: (value: unknown, record: Record<string, unknown>) => {
      const gid = record.gid;
      if (!editingEnabled || c.name.toLowerCase() === 'gid' || gid == null) {
        return renderCellValue(value);
      }
      if (c.numeric) {
        return (
          <InputNumber size="small" style={{ width: '100%' }} value={value as number | null} onChange={(v) => onCellChange(String(gid), c.name, v)} />
        );
      }
      return <Input size="small" value={value == null ? '' : String(value)} onChange={(e) => onCellChange(String(gid), c.name, e.target.value)} />;
    },
  }));

// Attribute table for the spatial preview. Read-only for a throwaway preview
// session; editable when the session is bound to a real published layer table
// (see `editable`), in which case saves go through the same update logic
// MapPortal's attribute table uses, so edits show up there too.
const PreviewAttributeTable: React.FC<PreviewAttributeTableProps> = ({ sessionId, title, onClose, editable = false }) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  // Guards against a burst of scroll events advancing the page more than once
  // before the in-flight fetch registers (would skip pages).
  const loadingMoreRef = useRef(false);
  const [page, setPage] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [columnDefs, setColumnDefs] = useState<ColumnDef[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tableScrollY, setTableScrollY] = useState(200);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // gid -> changed {column: value}, flushed to the server on Save.
  const editsRef = useRef<Map<string, Record<string, unknown>>>(new Map());

  const fetchPage = useCallback(
    async (pageNo: number) => {
      const url = generatePath(ApiRoutes.FILEMANAGER_SPATIAL_PREVIEW_FEATURES, { sessionId });
      const { data } = await axiosInstance.get<SpatialPreviewFeaturesResponse>(url, {
        params: { page: pageNo, pageSize: PAGE_SIZE },
      });
      return data;
    },
    [sessionId],
  );

  // Page 0 replaces; later pages append (deduped by gid) for infinite scroll.
  // refreshKey forces a page-0 reload after a save/cancel even when page is
  // already 0 (setPage(0) alone wouldn't change the dependency).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (page === 0) setLoading(true);
      else setLoadingMore(true);
      try {
        const data = await fetchPage(page);
        if (cancelled) return;
        // Column defs are stable across pages — only build once so edited-cell
        // identity doesn't churn while thousands of rows append.
        if (page === 0) {
          setColumnDefs(data.columns || []);
          setTotal(data.total || 0);
        }
        const mapped = (data.rows || []).map((row, i) => ({
          key: String((row as any).gid ?? `${page}-${i}`),
          ...row,
        }));
        setRows((prev) => {
          if (page === 0) return mapped;
          const seen = new Set(prev.map((r) => r.key));
          return [...prev, ...mapped.filter((r) => !seen.has(r.key))];
        });
      } catch (e) {
        console.error('Failed to load preview features', e);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, fetchPage, refreshKey]);

  const hasMore = rows.length > 0 && rows.length < total;

  useEffect(() => {
    const body = bodyRef.current?.querySelector('.ant-table-body') as HTMLElement | null;
    if (!body) return;
    const onScroll = () => {
      if (loadingMoreRef.current || !hasMore) return;
      if (body.scrollTop + body.clientHeight >= body.scrollHeight - 300) {
        loadingMoreRef.current = true;
        setPage((p) => p + 1);
      }
    };
    body.addEventListener('scroll', onScroll, { passive: true });
    return () => body.removeEventListener('scroll', onScroll);
  }, [hasMore]);

  // Size the scroll area to the card body so the header/footer stay put and only
  // the rows scroll (keeps the card at its fixed height).
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    let rafId: number;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const header = el.querySelector<HTMLElement>('.ant-table-header');
        const headerH = header?.offsetHeight ?? 40;
        setTableScrollY(Math.max(80, el.clientHeight - headerH - 40));
      });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  const handleCellChange = useCallback((gid: string, column: string, value: unknown) => {
    setRows((prev) => prev.map((r) => (String((r as any).gid) === gid ? { ...r, [column]: value } : r)));
    const existing = editsRef.current.get(gid) || {};
    editsRef.current.set(gid, { ...existing, [column]: value });
  }, []);

  const columns = useMemo(
    () => buildColumns(columnDefs, editable && isEditing, handleCellChange),
    [columnDefs, editable, isEditing, handleCellChange],
  );

  const reload = useCallback(() => {
    editsRef.current.clear();
    setPage(0);
    setRefreshKey((k) => k + 1);
  }, []);

  const handleToggleEdit = () => {
    if (isEditing) {
      // Turning edit mode off without saving discards any in-progress edits.
      reload();
    }
    setIsEditing((prev) => !prev);
  };

  const handleSave = async () => {
    if (editsRef.current.size === 0) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      const updates = Array.from(editsRef.current.entries()).map(([gid, properties]) => ({ gid, properties }));
      const url = generatePath(ApiRoutes.FILEMANAGER_SPATIAL_PREVIEW_FEATURES, { sessionId });
      await axiosInstance.patch(url, { updates });
      Notification({ message: 'Attribute table updated.', type: NotificationTypes.SUCCESS });
      setIsEditing(false);
      reload();
    } catch (e: any) {
      console.error('Failed to save preview feature edits', e);
      Notification({
        message: e?.response?.data?.message || 'Failed to save changes.',
        type: NotificationTypes.ERROR,
      });
    } finally {
      setSaving(false);
    }
  };

  // Mirror ViewAttributeTable: page appends show a small footer spinner so
  // already-loaded rows stay mounted (no full-table flash).
  const footer = useMemo(
    () => () => (
      <div className="previewAttributeTable__footer">
        {loadingMore ? (
          <span className="previewAttributeTable__footerLoading">
            <Spin size="small" /> Loading more…
          </span>
        ) : (
          <span>
            {rows.length} of {total} features
          </span>
        )}
      </div>
    ),
    [loadingMore, rows.length, total],
  );

  return (
    <div className="previewAttributeTable">
      <button type="button" className="previewAttributeTable__close" onClick={onClose} aria-label="Close table">
        <AiOutlineClose />
      </button>

      <div className="previewAttributeTable__header">
        <Title level={5} className="previewAttributeTable__title">
          {title}
        </Title>
        <Text className="previewAttributeTable__count">{total} FEATURES</Text>
        {editable && (
          <div className="previewAttributeTable__editActions">
            {isEditing && (
              <Button size="small" onClick={handleToggleEdit} disabled={saving}>
                Cancel
              </Button>
            )}
            <Button
              size="small"
              shape="circle"
              icon={isEditing ? <SaveIcon /> : <EditIcon />}
              title={isEditing ? 'Save changes' : 'Edit attributes'}
              loading={isEditing && saving}
              onClick={isEditing ? handleSave : handleToggleEdit}
            />
          </div>
        )}
      </div>

      <div className="previewAttributeTable__body" ref={bodyRef}>
        {loading && rows.length === 0 ? (
          <div className="previewAttributeTable__loading">
            <Spin />
          </div>
        ) : (
          <Table
            dataSource={rows}
            columns={columns}
            rowKey="key"
            pagination={false}
            bordered
            scroll={{ y: tableScrollY, x: 'max-content' }}
            className="custom-table bordered-table"
            footer={footer}
          />
        )}
      </div>
    </div>
  );
};

export default PreviewAttributeTable;
